import type { AgenticLogicEvaluationTelemetry } from "@intelligems/ig-types/functions/agentic-logic/types/telemetry";
import type { AgenticLogicOnEmpty } from "@intelligems/ig-types/dtos/agentic-logic/rules";
import type {
  AgenticLogicScenarioExclusionRemovalType,
  AgenticLogicScenarioHydratedItemType,
} from "@intelligems/ig-types/functions/agentic-logic/rule-scenarios/rule-scenarios";
import type { RuleGraphCoverage } from "@intelligems/ig-types/dtos/agentic-logic/rule-graph-coverage";
import type { RuleGraphDto } from "@intelligems/ig-types/dtos/agentic-logic/rule-graph";

import { analyzeRuleGraph } from "./analysis";
import type { RuleGraphScenarioContext } from "./scenario-context";
import {
  edgeOverlayId,
  type RuleGraphNodeOverlay,
  type RuleGraphOverlaysByEdgeId,
  type RuleGraphOverlaysByNodeId,
} from "./types";

export interface RuleGraphScenarioOverlays {
  byNodeId: RuleGraphOverlaysByNodeId;
  byEdgeId: RuleGraphOverlaysByEdgeId;
}

const EXCLUSION_REASON_LABELS: Record<
  AgenticLogicScenarioExclusionRemovalType["reason"],
  string
> = {
  product_id: "product",
  tag: "tag",
  collection: "collection",
  vendor: "vendor",
  metadata_unavailable: "metadata unavailable",
};

function exclusionBodyText(
  removals: AgenticLogicScenarioExclusionRemovalType[]
): string {
  if (removals.length === 0) {
    return "removed 0";
  }
  const first = removals[0];
  const value =
    first === undefined
      ? "metadata unavailable"
      : (first.matchedValue ?? EXCLUSION_REASON_LABELS[first.reason]);
  const remaining = removals.length - 1;
  return `removed ${removals.length} · ${value}${remaining > 0 ? ` + ${remaining} more` : ""}`;
}

const ordinal = (n: number): string => {
  if (n === 1) {
    return "first";
  }
  if (n === 2) {
    return "second";
  }
  if (n === 3) {
    return "third";
  }
  return `${n}th`;
};

/**
 * Pure join of one scenario evaluation onto the graph's overlay seams.
 *
 * Trust boundaries, in order: `coverage` is the server-computed truth about
 * traversal (its unknown-id tolerance already happened server-side);
 * `telemetry.attempts` decorates branch nodes it can name and silently skips
 * bases the graph lacks — telemetry and graph come from different revisions
 * and may briefly disagree, and a half-annotated graph beats a crashed one.
 * Returns null when there is nothing joinable (no coverage), so callers
 * render the plain graph rather than a broken half-dim.
 */
export function buildScenarioOverlays({
  graph,
  coverage,
  telemetry,
  exclusions,
  hydratedItems,
  onEmpty,
  imageUrlByGid,
  scenarioContext,
}: {
  graph: RuleGraphDto;
  coverage: RuleGraphCoverage | null;
  telemetry: AgenticLogicEvaluationTelemetry;
  exclusions?: {
    evaluated: boolean;
    removals: AgenticLogicScenarioExclusionRemovalType[];
  };
  hydratedItems: AgenticLogicScenarioHydratedItemType[];
  /** The rule's empty-result policy — what the empty face says the shopper sees. */
  onEmpty: AgenticLogicOnEmpty;
  /**
   * Thumbnails already resolved from the hydrated items' GIDs, keyed by the
   * GID that was asked for. Items whose picture has not landed yet (or has
   * none) simply render the placeholder.
   */
  imageUrlByGid: Record<string, string | null>;
  /** The run's request, said back on both faces of "what the shopper sees". */
  scenarioContext?: RuleGraphScenarioContext;
}): RuleGraphScenarioOverlays | null {
  if (coverage === null) {
    return null;
  }
  const nodeIds = new Set(graph.nodes.map((node) => node.id));
  if (coverage.terminalNodeId && !nodeIds.has(coverage.terminalNodeId)) {
    // The coverage joins to a different graph revision — no overlays beat
    // overlays that dim the wrong lane.
    return null;
  }

  const traversed = new Set(coverage.traversedNodeIds);
  const byNodeId: RuleGraphOverlaysByNodeId = {};
  const overlayFor = (nodeId: string): RuleGraphNodeOverlay =>
    (byNodeId[nodeId] ??= {});

  const exclusionsNode = graph.nodes.find((node) => node.kind === "exclusions");
  if (exclusionsNode && exclusions?.evaluated) {
    overlayFor(exclusionsNode.id).bodyText = exclusionBodyText(
      exclusions.removals
    );
  }

  for (const node of graph.nodes) {
    if (!traversed.has(node.id)) {
      overlayFor(node.id).dimmed = true;
    }
  }

  const attempts = telemetry.attempts ?? [];
  const contributedByBase = new Map<string, number>();
  for (const attempt of attempts) {
    contributedByBase.set(
      attempt.base,
      (contributedByBase.get(attempt.base) ?? 0) + attempt.contributed
    );
  }
  for (const [branchId, state] of Object.entries(coverage.branchStates)) {
    if (!nodeIds.has(branchId)) {
      continue;
    }
    const overlay = overlayFor(branchId);
    if (state === "fired") {
      const contributed = contributedByBase.get(branchId);
      overlay.chip = {
        label:
          contributed !== undefined
            ? `matched · ${contributed} product${contributed === 1 ? "" : "s"}`
            : "matched",
        color: "success",
      };
    } else if (state === "attempted") {
      overlay.chip = { label: "no match", color: "default" };
    }
  }
  // Raw attempt records for the details panel, on the branch nodes they name.
  for (const attempt of attempts) {
    if (!nodeIds.has(attempt.base)) {
      continue;
    }
    (overlayFor(attempt.base).attempts ??= []).push(attempt);
  }

  // "What the shopper sees" hangs off the node the evaluation ended on: the
  // OUTPUT node when a step fired (the Figma right-of-the-lane column), and
  // the FALLBACK TERMINAL when nothing matched — the terminal row IS the
  // nothing outcome, so its card slot renders the empty face in place of the
  // plain "Hide the upsell" card rather than floating a panel in an output
  // column the evaluation never reached.
  if (coverage.terminalNodeId) {
    const terminal = graph.nodes.find(
      (node) => node.id === coverage.terminalNodeId
    );
    const output = graph.nodes.find((node) => node.kind === "output");
    const carrier = terminal?.kind === "fallback-terminal" ? terminal : output;
    if (carrier) {
      const overlay = overlayFor(carrier.id);
      overlay.result =
        terminal?.kind === "fallback-terminal"
          ? {
              products: [],
              explanation: buildEmptyExplanation(coverage, onEmpty),
              empty: true,
              onEmpty,
              ...(scenarioContext ? { scenarioContext } : {}),
            }
          : {
              products: hydratedItems.map((item) => ({
                id: item.id,
                title: item.title,
                variantTitle: item.variantTitle ?? null,
                price: item.price,
                // The variant is the more specific picture when the envelope
                // pre-selected one; the product's featured image stands in
                // otherwise, and the resolver already folded that fallback in.
                imageUrl: imageUrlByGid[item.variantId ?? item.id] ?? null,
              })),
              explanation: buildExplanation(graph, coverage, contributedByBase),
              empty: false,
              onEmpty,
              ...(scenarioContext ? { scenarioContext } : {}),
            };
      // The panel reports the evaluation's answer rather than a step the
      // cart walked through, so it reads at full strength either way.
      delete overlay.dimmed;
    }
  }

  const byEdgeId: RuleGraphOverlaysByEdgeId = {};
  for (const edge of analyzeRuleGraph(graph).graph.edges) {
    if (!nodeIds.has(edge.from) || !nodeIds.has(edge.to)) {
      continue;
    }
    if (!traversed.has(edge.from) || !traversed.has(edge.to)) {
      byEdgeId[edgeOverlayId(edge)] = { dimmed: true };
    }
  }

  return { byNodeId, byEdgeId };
}

/**
 * The empty panel's second line. The dashed box already says *what* the
 * shopper sees (the onEmpty policy's answer); this says *why*, so the two
 * lines never repeat each other the way a shared string would.
 */
function buildEmptyExplanation(
  coverage: RuleGraphCoverage,
  onEmpty: AgenticLogicOnEmpty
): string {
  const consequence =
    onEmpty === "smart-recs"
      ? "smart recommendations fill the slot instead"
      : "the upsell block is hidden";
  const attempted = Object.values(coverage.branchStates).filter(
    (state) => state === "attempted"
  ).length;
  if (attempted === 0) {
    return `No step was reached, so the When gate never opened and ${consequence}.`;
  }
  if (attempted === 1) {
    return `The one step that ran matched nothing, so ${consequence}.`;
  }
  return `All ${attempted} steps ran and none matched, so ${consequence}.`;
}

/**
 * Branch-level copy — per-step funnel counts deliberately do not exist, so
 * the explanation names the step that matched and what it contributed.
 */
function buildExplanation(
  graph: RuleGraphDto,
  coverage: RuleGraphCoverage,
  contributedByBase: Map<string, number>
): string {
  const firedBranchId = coverage.firedBranchId;
  if (firedBranchId === null) {
    return "Every filter passed — these products made it through.";
  }
  const branch = graph.nodes.find((node) => node.id === firedBranchId);
  const label = branch?.label ?? firedBranchId;
  const contributed = contributedByBase.get(firedBranchId);
  const { priorityByBranchId } = analyzeRuleGraph(graph);
  const priority = priorityByBranchId.get(firedBranchId);
  const stepPhrase =
    priority !== undefined
      ? `The ${ordinal(priority)} step matched`
      : "This step matched";
  const contribution =
    contributed !== undefined
      ? ` contributed ${contributed} product${contributed === 1 ? "" : "s"}`
      : " matched";
  return `${stepPhrase}: ${label}${contribution}.`;
}
